import { OperatorStage } from './OperatorStage.js';
import { operatorFor } from '../gameplay/operators.js';
import { vtext, chamfer, bracket, CO, damp, easeOut } from './HUD.js';

/**
 * The end-of-match screens: who won, and who did what.
 *
 * ## Why this is drawn on the HUD canvas and not in the DOM
 *
 * A DOM overlay would be less code and it would be wrong. The game holds
 * pointer lock; to click a DOM button the page has to release it, and releasing
 * it pauses the offline game loop — so the mode clock that starts the NEXT
 * match would stop, and the award screen would be the last thing that ever
 * happened. Drawing into the HUD's 2D context keeps the lock, keeps the loop,
 * and lets the pages be flicked with the arrow keys the game already reads.
 *
 * The character model is the one thing 2D canvas cannot do, so `OperatorStage`
 * renders it into its own WebGL canvas and this blits that canvas in. The stage
 * exists only while the screen is up: it costs a GL context, and the browser
 * has about sixteen.
 */

/**
 * Seconds a page holds before advancing on its own.
 *
 * Sized against the intermission, not chosen for feel: the result page plus at
 * most six awards is seven pages, and both the server and the offline mode give
 * the screens 26 seconds (4 s of post-round plus a 22 s hold). At four seconds
 * a page the last one would never come up on its own.
 */
const PAGE_TIME = 3.2;

export class MatchAwards {
  constructor() {
    this.active = false;
    this.page = 0;
    this.pages = [];
    this.data = null;
    this._pageT = 0;
    this._auto = true;
    this._ease = 0;
    this._flip = 1;
    this.stage = null;
  }

  /**
   * @param {{
   *   winner:'red'|'blue'|'draw', red:number, blue:number, mine:?string,
   *   ffa?:boolean, awards:Array, rows:Array,
   * }} data
   */
  show(data) {
    // Re-showing the same match must not restart the flick-through — the
    // server repeats the award table in every snapshot so a late joiner gets
    // one, and acting on each repeat would pin the screen to page one forever.
    if (this.active && this.data && sameMatch(this.data, data)) return;
    this.data = data;
    this.pages = [{ kind: 'result' }, ...(data.awards ?? []).map((a) => ({ kind: 'award', a }))];
    this.page = 0;
    this._pageT = 0;
    this._auto = true;
    this._flip = 1;
    this.active = true;
    this.stage ??= new OperatorStage({ width: 460, height: 690, dpr: 1 });
  }

  hide() {
    this.active = false;
    this.data = null;
    this.pages = [];
    this.stage?.dispose();
    this.stage = null;
  }

  /** Arrow keys page through; touching them stops the auto-advance. */
  update(dt, input) {
    if (!this.active) return;
    this._ease = damp(this._ease, 1, 7, dt);
    if (input?.pressed?.('ArrowRight') || input?.pressed?.('KeyD')) this.step(1);
    else if (input?.pressed?.('ArrowLeft') || input?.pressed?.('KeyA')) this.step(-1);
    else if (this._auto) {
      this._pageT += dt;
      if (this._pageT >= PAGE_TIME) {
        this._pageT = 0;
        // Auto-advance STOPS at the last page rather than looping. Looping a
        // result screen makes it impossible to tell whether you have seen
        // everything, and there is nothing else to do with the time.
        if (this.page < this.pages.length - 1) { this.page++; this._flip = 1; }
      }
    }
  }

  step(d) {
    this._auto = false;
    const n = this.pages.length;
    this.page = ((this.page + d) % n + n) % n;
    this._pageT = 0;
    this._flip = d;
  }

  /** @param {CanvasRenderingContext2D} ctx */
  draw(ctx, w, h, s) {
    if (!this.active || !this.pages.length) return;
    const page = this.pages[Math.min(this.page, this.pages.length - 1)];
    const e = easeOut(this._ease);

    ctx.save();
    ctx.globalAlpha = e;
    // A near-solid wash. Unlike the per-round banner this is not something to
    // watch the world through: the match is over, nothing is happening out
    // there, and half-transparency would only make the text harder to read.
    // Nearly opaque. The HUD is a separate canvas over the GL one, so whatever
    // this does not cover is the live world — and at 0.90 the viewmodel's arms
    // and gun still read clearly through the right-hand half of the screen.
    ctx.fillStyle = 'rgba(3,6,10,0.955)';
    ctx.fillRect(0, 0, w, h);

    this._drawHeader(ctx, w, h, s);
    if (page.kind === 'result') this._drawResult(ctx, w, h, s);
    else this._drawAward(ctx, w, h, s, page.a);
    this._drawPager(ctx, w, h, s);
    ctx.restore();
  }

  /* ------------------------------------------------------------ pieces -- */

  _drawHeader(ctx, w, h, s) {
    const d = this.data;
    if (d.ffa) return this._drawFfaHeader(ctx, w, h, s);
    const draw = d.winner === 'draw';
    const side = d.winner === 'red' ? 'ATTACKERS' : 'DEFENDERS';
    const col = draw ? CO.hi
      : d.winner === 'red' ? 'rgba(255,120,96,0.98)' : 'rgba(120,180,255,0.98)';
    const cx = w / 2;

    vtext(ctx, 'MATCH OVER', cx, 62 * s, 11 * s,
      { color: CO.faint, weight: 0.17, track: 9 * s, align: 'center' });
    vtext(ctx, draw ? 'DRAW' : `${side} WIN`, cx, 108 * s, 34 * s,
      { color: col, weight: 0.12, track: 10 * s, align: 'center' });
    // The score always reads MY SIDE first when there is one. "3 - 4" means
    // nothing until you know which number is yours.
    const mine = d.mine;
    const a = mine === 'blue' ? d.blue : d.red;
    const b = mine === 'blue' ? d.red : d.blue;
    vtext(ctx, `${a} - ${b}`, cx, 146 * s, 20 * s,
      { color: CO.hi, weight: 0.13, track: 6 * s, align: 'center' });
  }

  /**
   * Free-for-all has no sides, so it gets its own header: the winner is a
   * PERSON, and "ATTACKERS WIN" would be describing a distinction the mode does
   * not have.
   */
  _drawFfaHeader(ctx, w, h, s) {
    const d = this.data;
    const cx = w / 2;
    vtext(ctx, 'MATCH OVER', cx, 62 * s, 11 * s,
      { color: CO.faint, weight: 0.17, track: 9 * s, align: 'center' });
    vtext(ctx, clip(d.winnerName, 16), cx, 108 * s, 34 * s,
      { color: CO.accent, weight: 0.12, track: 10 * s, align: 'center' });
    vtext(ctx, `${d.winnerScore ?? 0} KILLS`, cx, 146 * s, 18 * s,
      { color: CO.hi, weight: 0.13, track: 6 * s, align: 'center' });
  }

  /** Page one: the final scoreboard, both sides. */
  _drawResult(ctx, w, h, s) {
    const d = this.data;
    if (d.ffa) return this._drawFfaResult(ctx, w, h, s);
    const rows = [...(d.rows ?? [])].sort((x, y) => (y.kills ?? 0) - (x.kills ?? 0));
    const cols = [
      { team: 'red', title: 'ATTACKERS', col: 'rgba(255,120,96,0.95)' },
      { team: 'blue', title: 'DEFENDERS', col: 'rgba(120,180,255,0.95)' },
    ];
    const panelW = Math.min(430 * s, w * 0.40);
    const gap = 26 * s;
    const rowH = 28 * s;
    // Sized to the fuller of the two sides, then centred, so the panels are not
    // pinned under the header with the rest of the screen left empty.
    const tallest = Math.max(1, ...cols.map((c) => rows.filter((r) => r.team === c.team).length));
    const top = Math.max(200 * s, (h - (54 * s + rowH * tallest)) / 2);

    for (let i = 0; i < 2; i++) {
      const c = cols[i];
      const x = w / 2 + (i === 0 ? -panelW - gap / 2 : gap / 2);
      const list = rows.filter((r) => r.team === c.team);
      const panelH = Math.max(120 * s, 54 * s + rowH * Math.max(1, list.length));

      ctx.fillStyle = 'rgba(10,15,21,0.72)';
      chamfer(ctx, x, top, panelW, panelH, 10 * s);
      ctx.fill();
      bracket(ctx, x, top, panelW, panelH, 14 * s, 'rgba(255,255,255,0.14)', 1.4 * s);

      vtext(ctx, c.title, x + 16 * s, top + 28 * s, 12 * s,
        { color: c.col, weight: 0.16, track: 5 * s });
      vtext(ctx, 'K   D   DMG', x + panelW - 16 * s, top + 28 * s, 9 * s,
        { color: CO.faint, weight: 0.17, track: 3.4 * s, align: 'right' });

      let y = top + 54 * s;
      if (!list.length) {
        vtext(ctx, 'NOBODY', x + 16 * s, y + 14 * s, 11 * s,
          { color: 'rgba(160,176,192,0.5)', weight: 0.15, track: 3.5 * s });
      }
      for (const r of list) {
        const self = r.self;
        vtext(ctx, clip(r.name, 14), x + 16 * s, y + 16 * s, 12 * s,
          { color: self ? CO.accent : CO.hi, weight: 0.14, track: 3.6 * s });
        vtext(ctx, `${r.kills ?? 0}   ${r.deaths ?? 0}   ${Math.round(r.damage ?? 0)}`,
          x + panelW - 16 * s, y + 16 * s, 11 * s,
          { color: CO.dim, weight: 0.15, track: 3.4 * s, align: 'right' });
        y += rowH;
      }
    }
  }

  /** Free-for-all's final table: one ranked list, because there are no sides. */
  _drawFfaResult(ctx, w, h, s) {
    const rows = [...(this.data.rows ?? [])]
      .sort((x, y) => (y.kills ?? 0) - (x.kills ?? 0))
      .slice(0, 10);
    const panelW = Math.min(620 * s, w * 0.62);
    const rowH = 28 * s;
    const panelH = 54 * s + rowH * Math.max(1, rows.length);
    const x = (w - panelW) / 2;
    const top = Math.max(196 * s, (h - panelH) / 2);

    ctx.fillStyle = 'rgba(10,15,21,0.72)';
    chamfer(ctx, x, top, panelW, panelH, 10 * s);
    ctx.fill();
    bracket(ctx, x, top, panelW, panelH, 14 * s, 'rgba(255,255,255,0.14)', 1.4 * s);

    vtext(ctx, 'FINAL STANDINGS', x + 16 * s, top + 28 * s, 12 * s,
      { color: CO.accent, weight: 0.16, track: 5 * s });
    vtext(ctx, 'K   D   DMG', x + panelW - 16 * s, top + 28 * s, 9 * s,
      { color: CO.faint, weight: 0.17, track: 3.4 * s, align: 'right' });

    let y = top + 54 * s;
    rows.forEach((r, i) => {
      vtext(ctx, String(i + 1).padStart(2, '0'), x + 16 * s, y + 16 * s, 11 * s,
        { color: 'rgba(160,176,192,0.55)', weight: 0.15, track: 3.2 * s });
      vtext(ctx, clip(r.name, 16), x + 54 * s, y + 16 * s, 12 * s,
        { color: r.self ? CO.accent : CO.hi, weight: 0.14, track: 3.6 * s });
      vtext(ctx, `${r.kills ?? 0}   ${r.deaths ?? 0}   ${Math.round(r.damage ?? 0)}`,
        x + panelW - 16 * s, y + 16 * s, 11 * s,
        { color: CO.dim, weight: 0.15, track: 3.4 * s, align: 'right' });
      y += rowH;
    });
  }

  /** Every other page: one award, one model, one name. */
  _drawAward(ctx, w, h, s, a) {
    const d = this.data;
    const win = a.winner;
    // In a free-for-all the team a row happens to carry is bookkeeping, not a
    // side anyone is on, so colouring a name by it would be inventing teams.
    const teamCol = d.ffa ? 'rgba(255,196,74,0.98)'
      : win.team === 'red' ? 'rgba(255,120,96,0.98)' : 'rgba(120,180,255,0.98)';

    // ---- model
    //
    // The block is sized from the CONTENT, not from the viewport: a model box
    // plus a gutter plus a column of words. Centring the pair is what stops the
    // page sitting off to one side, which is what it did when the model was
    // placed at a fixed fraction of the screen.
    const boxW = Math.min(400 * s, w * 0.26);
    const boxH = boxW * 1.5;
    const gutter = 52 * s;
    const textW = Math.min(520 * s, w * 0.34);
    const bx = Math.max(24 * s, (w - (boxW + gutter + textW)) / 2);
    const by = Math.max(184 * s, (h - boxH) / 2 - 10 * s);
    if (this.stage) {
      // The same pool of light the operator picker stands them in, so the model
      // does not read as a cut-out pasted onto black.
      const bg = ctx.createRadialGradient(
        bx + boxW / 2, by + boxH * 0.28, 0,
        bx + boxW / 2, by + boxH * 0.28, boxW * 0.85);
      bg.addColorStop(0, 'rgba(40,56,74,0.55)');
      bg.addColorStop(1, 'rgba(8,12,18,0)');
      ctx.fillStyle = bg;
      ctx.fillRect(bx - boxW * 0.3, by, boxW * 1.6, boxH);
    }
    if (this.stage) {
      this.stage.show(operatorFor(win.op, { team: win.team, ffa: !!d.ffa }), { ffa: !!d.ffa });
      this.stage.pivot.rotation.y = this.stage.baseYaw + Math.sin(performance.now() * 0.00022) * 0.5;
      this.stage.renderer.render(this.stage.scene, this.stage.camera);
      ctx.save();
      // A faint pedestal so the figure has somewhere to stand.
      const g = ctx.createRadialGradient(bx + boxW / 2, by + boxH * 0.92, 0,
        bx + boxW / 2, by + boxH * 0.92, boxW * 0.55);
      g.addColorStop(0, `${teamCol.slice(0, -4)}0.20)`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(bx, by + boxH * 0.6, boxW, boxH * 0.5);
      try {
        ctx.drawImage(this.stage.renderer.domElement, bx, by, boxW, boxH);
      } catch { /* context lost — the words still work */ }
      ctx.restore();
    }

    // ---- words
    const tx = bx + boxW + gutter;
    let ty = by + 78 * s;
    vtext(ctx, a.title, tx, ty, 26 * s, { color: CO.accent, weight: 0.13, track: 8 * s });
    ty += 26 * s;
    vtext(ctx, a.sub.toUpperCase(), tx, ty, 9.5 * s,
      { color: CO.faint, weight: 0.17, track: 4.6 * s });

    ty += 66 * s;
    vtext(ctx, clip(win.name, 16), tx, ty, 34 * s, { color: teamCol, weight: 0.12, track: 8 * s });
    if (!d.ffa) {
      ty += 24 * s;
      vtext(ctx, win.team === 'red' ? 'ATTACKERS' : 'DEFENDERS', tx, ty, 9 * s,
        { color: CO.faint, weight: 0.17, track: 4.6 * s });
    }

    ty += 62 * s;
    vtext(ctx, a.stat, tx, ty, 22 * s, { color: CO.hi, weight: 0.13, track: 6 * s });

    // ---- who nearly had it
    if (a.runners?.length) {
      ty += 46 * s;
      vtext(ctx, 'RUNNERS UP', tx, ty, 8.5 * s,
        { color: 'rgba(160,176,192,0.55)', weight: 0.18, track: 4.4 * s });
      for (const r of a.runners) {
        ty += 24 * s;
        vtext(ctx, clip(r.name, 14), tx, ty, 12 * s,
          { color: CO.dim, weight: 0.15, track: 3.6 * s });
        vtext(ctx, r.stat, tx + textW * 0.55, ty, 12 * s,
          { color: 'rgba(160,176,192,0.7)', weight: 0.15, track: 3.6 * s });
      }
    }
  }

  /** Dots and the hint that these are switchable at all. */
  _drawPager(ctx, w, h, s) {
    const n = this.pages.length;
    const cy = h - 62 * s;
    const gap = 18 * s;
    const x0 = w / 2 - ((n - 1) * gap) / 2;
    for (let i = 0; i < n; i++) {
      const on = i === this.page;
      ctx.beginPath();
      ctx.arc(x0 + i * gap, cy, (on ? 4.6 : 3.0) * s, 0, Math.PI * 2);
      ctx.fillStyle = on ? CO.accent : 'rgba(160,176,192,0.32)';
      ctx.fill();
    }
    vtext(ctx, '‹ ›  ARROW KEYS', w / 2, h - 34 * s, 8.5 * s,
      { color: 'rgba(160,176,192,0.5)', weight: 0.18, track: 4.4 * s, align: 'center' });
  }
}

/** Trim a name that would run into the next column. */
function clip(str, n) {
  const t = String(str ?? '').toUpperCase();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

/**
 * Is this the same finished match we are already showing?
 *
 * Compared on the score and the award winners rather than on an id, because
 * there is no match id anywhere — and adding one to the wire just to answer
 * this would be a protocol change to solve a screen's problem.
 */
function sameMatch(a, b) {
  if (a.winner !== b.winner || a.winnerName !== b.winnerName) return false;
  if (!a.ffa && (a.red !== b.red || a.blue !== b.blue)) return false;
  const ka = (a.awards ?? []).map((x) => `${x.id}:${x.winner?.id}`).join('|');
  const kb = (b.awards ?? []).map((x) => `${x.id}:${x.winner?.id}`).join('|');
  return ka === kb;
}
